import { useEffect, useRef } from 'react'

import type { StepClip as StepClipData } from '../steps'

interface StepClipProps {
  clip: StepClipData
  title: string
}

const MEDIA = 'aspect-video w-full rounded-md object-cover'
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)'

// A short muted loop of the real app, or its still until the clip exists.
// The stills share the app's page colour, so the panel reads as one surface.
export function StepClip({ clip, title }: StepClipProps) {
  return (
    <div className="rounded-xl bg-background p-3 sm:p-4">
      <StepMedia clip={clip} title={title} />
    </div>
  )
}

function StepMedia({ clip, title }: StepClipProps) {
  const prefersStill = window.matchMedia(REDUCED_MOTION).matches
  if (clip.video === null || prefersStill) {
    return <img src={clip.still} alt={title} className={MEDIA} />
  }
  return <LoopingVideo src={clip.video} poster={clip.still} title={title} />
}

interface LoopingVideoProps {
  src: string
  poster: string
  title: string
}

// Six clips on one page: each plays only while it is on screen.
function LoopingVideo({ src, poster, title }: LoopingVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) {
        video.play().catch(() => {})
      } else {
        video.pause()
      }
    })
    observer.observe(video)
    return () => observer.disconnect()
  }, [])

  return (
    <video
      ref={videoRef}
      className={MEDIA}
      src={src}
      poster={poster}
      aria-label={title}
      muted
      loop
      playsInline
      preload="metadata"
    />
  )
}
